import React, { useCallback, useEffect, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Flex, Select, Tag, Typography } from '@duxton-web/ui';
import { debounce } from 'lodash';

import { isMoveITTenant } from '../Tenants';
import { FacetToSegpAppModeMapping, searchSegment } from './helper';
import { FACETS } from '@/constants/facets';
import { Constraint } from '@/lib/PlannerDTOs';

import './SegmentSelector.css';

import { SegmentNameSpace } from '../valueItemType';
import Service from '@/lib/App';

const { Option } = Select;

interface SegmentSelectorProps {
  value?: string[];
  namespace?: string;
  mode?: 'multiple' | 'tags';
  constraint?: Constraint;
  placeholder?: string;
  wrapperStyle?: React.CSSProperties;
  segmentNameSelectorStyle?: React.CSSProperties;
  namespaceSelectorStyle?: React.CSSProperties;
  disabled?: boolean;
  onChangeSegment: (value: string[]) => void;
  onChangeNamespace: (value: string) => void;
  bordered?: boolean;
}

export const NameSpace = {
  MEXUSER: 'mexuser',
  MEXSTORE: 'mexstore',
  MEXMERCHANT: 'mexmerchant',
  MOVEIT: 'moveit',
};

export function getAvailableNamespaces(constraint: Constraint): string[] {
  if (isMoveITTenant()) {
    return ['moveit'];
  }

  const overrideNamespaces: Record<string, string[]> =
    Service.cache.getOverrideSegpNamespaces();
  const segpNamespaces = Service.cache.getSegpNamespaces() || [];

  // Derive candidate namespaces: prefer per-app-mode overrides, fall back to
  // the generic segpNamespaces list when overrides are unavailable.
  const candidateNamespaces =
    overrideNamespaces && constraint?.target
      ? overrideNamespaces[FacetToSegpAppModeMapping[constraint.target]]
      : segpNamespaces;

  if (!candidateNamespaces || candidateNamespaces.length === 0) {
    // If overrides exist but the app mode has no entry, no namespaces available.
    // If overrides are null and segpNamespaces is empty, same result.
    return overrideNamespaces && constraint?.target ? [] : segpNamespaces;
  }

  // Apply facet-specific namespace restrictions regardless of data source.
  // mes facet can only use mexstore namespace
  if (constraint?.target === FACETS.MexStore) {
    return candidateNamespaces.includes(NameSpace.MEXSTORE)
      ? [NameSpace.MEXSTORE]
      : [];
  }
  // meu facet can only use mexuser namespace
  if (constraint?.target === FACETS.MexUser) {
    return candidateNamespaces.includes(NameSpace.MEXUSER)
      ? [NameSpace.MEXUSER]
      : [];
  }
  // mem facet can only use mexmerchant namespace
  if (constraint?.target === FACETS.MexMerchant) {
    return candidateNamespaces.includes(NameSpace.MEXMERCHANT)
      ? [NameSpace.MEXMERCHANT]
      : [];
  }
  // Exclude facet-specific namespaces from the generic list so that mex/pax/dax
  // constraints never see mexuser, mexstore, mexmerchant, or moveit in their
  // namespace dropdown. Each is exclusively gated to its own facet above.
  return candidateNamespaces.filter(
    (namespace) =>
      namespace !== NameSpace.MEXUSER &&
      namespace !== NameSpace.MEXSTORE &&
      namespace !== NameSpace.MEXMERCHANT &&
      namespace !== NameSpace.MOVEIT,
  );
}

const SegmentSelector: React.FC<SegmentSelectorProps> = ({
  value,
  namespace,
  mode,
  constraint,
  placeholder,
  wrapperStyle,
  segmentNameSelectorStyle,
  namespaceSelectorStyle,
  disabled,
  onChangeSegment,
  onChangeNamespace,
}) => {
  const [selectedNamespace, setSelectedNamespace] = useState<string>(
    namespace || null,
  );
  const [segmentSearchQuery, setSegmentSearchQuery] = useState('');
  const [searchingSegment, setSearchingSegment] = useState(false);
  const [segmentSearchResults, setSegmentSearchResults] = useState([]);
  const availableNamespaces = getAvailableNamespaces(constraint);

  const debouncedSearchSegments = useCallback(
    debounce(
      async ({
        query,
        namespace,
        facet,
      }: {
        query: string;
        namespace: string;
        facet: string;
      }) => {
        if (query.trim().length < 3) return;
        setSearchingSegment(true);
        const res = await searchSegment({ facet, namespace, query });
        setSearchingSegment(false);
        setSegmentSearchResults(res?.segmentList || []);
      },
      500,
    ),
    [],
  );

  function onSegmentChange(values: string[]): void {
    if (selectedNamespace !== SegmentNameSpace.SHARED) {
      onChangeSegment(values.map((val) => `${selectedNamespace}.${val}`));
    } else onChangeSegment(values);
  }

  useEffect(() => {
    if (!constraint?.target) return;
    if (!selectedNamespace) return;

    debouncedSearchSegments({
      query: segmentSearchQuery,
      namespace: selectedNamespace,
      facet: constraint?.target,
    });
  }, [segmentSearchQuery, selectedNamespace, constraint?.target]);

  // fetch namespace for initial render
  useEffect(() => {
    (async () => {
      if (!value?.length) return;

      if (value.length === 0) return;

      if (namespace) {
        setSelectedNamespace(namespace);
        return;
      }

      const segmentNameToSearch = value[0].split(':')[0];

      const res = await searchSegment({
        facet: constraint.target,
        query: segmentNameToSearch,
      });
      if (!res?.segmentList?.length) return;
      const ns = res.segmentList[0].namespace;
      setSelectedNamespace(ns);
    })();
  }, []);

  function renderOptions() {
    if (!segmentSearchQuery)
      return [
        <Option key="default" disabled>
          Enter at least 3 characters to search
        </Option>,
      ];

    return segmentSearchResults.map((result) => (
      <Option
        key={`${result.segmentName}${
          constraint.target === 'dax' ? `.${result.segmentID}` : ''
        }`}
        value={`${result.segmentName}${
          constraint.target === 'dax' ? `.${result.segmentID}` : ''
        }`}
      >
        <div>
          <Typography.Text color="#3D3D3D" strong>
            {result.segmentName}
          </Typography.Text>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <Typography.Text type="secondary">{result.createdBy}</Typography.Text>
          {result.properties?.creationType && (
            <Tag bordered={false} style={{ fontWeight: 700 }}>
              {result.properties.creationType}
            </Tag>
          )}
        </div>
      </Option>
    ));
  }

  return (
    <Flex
      align="center"
      wrap="wrap"
      className="segmentSelector"
      style={{ width: '100%', ...wrapperStyle }}
    >
      <Select
        style={{ minWidth: '180px', ...namespaceSelectorStyle }}
        onChange={(value) => {
          setSelectedNamespace(value);
          setSegmentSearchResults([]);
          onChangeNamespace(value);
        }}
        placeholder="Select namespace"
        disabled={disabled}
        value={selectedNamespace}
      >
        {availableNamespaces.map((item) => (
          <Option key={item} value={item}>
            <div>{item}</div>
          </Option>
        ))}
      </Select>

      <Select
        allowClear
        showSearch
        value={!mode ? value[0] : value}
        style={{ minWidth: 300, ...segmentNameSelectorStyle }}
        mode={mode}
        onChange={(values) => {
          if (!values) return onSegmentChange([]);
          if (typeof values === 'string') onSegmentChange([values]);
          if (Array.isArray(values)) onSegmentChange(values);
        }}
        optionLabelProp="label"
        placeholder={placeholder || 'Select segment'}
        disabled={disabled}
        loading={searchingSegment}
        popupMatchSelectWidth={false}
        onSearch={(value) => setSegmentSearchQuery(value)}
        tagRender={(tagProps) => (
          <Tag
            closable={!disabled}
            bordered={disabled}
            closeIcon={
              <CloseOutlined onClick={tagProps.onClose} {...({} as any)} />
            }
            style={{
              borderRadius: 4,
              color: '#3d3d3d',
              background: '#f5f5f5',
              fontSize: 14,
            }}
          >
            {tagProps.value}
          </Tag>
        )}
      >
        {renderOptions()}
      </Select>
    </Flex>
  );
};

export default SegmentSelector;
